/*
 * ॐ नमः शिवाय
 * ಓಂ ನಮಃ ಶಿವಾಯ
 * ఓం నమః శివాయ
 * ஓம் நம சிவாய
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms 
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this 
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and 
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

/**
 * ForgotPasswordForm
 * ------------------
 * A themed form for requesting a password reset link.
 *
 * Features:
 * • Username/Email input with validation
 * • Inline validation errors that fade out like SignInForm
 * • Loading state on submit button
 * • Success message display on successful request
 *
 * Props:
 * - onSubmit(form, setErrors, setSuccessMessage): handles API call at page level.
 */

import React, { useState, useEffect, useRef } from 'react';
import { useTheme } from '../theme/ThemeContext';
import envConfig from '../config/envConfig';
import { useAppBranding } from '../hooks/useAppBranding';
import Button from './Button';
import { useNavigate } from 'react-router-dom';
import VelLogo from './VelLogo';
import '../styles/EdgeHacks.css';
import { getPostAuthDestination } from '../utils/postAuthDestination';

function ForgotPasswordForm({ onSubmit }) {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const { data: branding } = useAppBranding();

  const [form, setForm] = useState({ identifier: '' });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const errorTimeouts = useRef({});

  const clearErrorAfterDelay = (field, delay = 4000) => {
    if (errorTimeouts.current[field]) clearTimeout(errorTimeouts.current[field]);
    errorTimeouts.current[field] = setTimeout(() => {
      setErrors(prev => ({ ...prev, [field]: '' }));
      delete errorTimeouts.current[field];
    }, delay);
  };

  const handleChange = (e) => {
    setForm(prev => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    let errs = {};

    if (!form.identifier.trim()) {
      errs.identifier = "Username or email is required.";
    }

    setErrors(errs);
    if (Object.keys(errs).length > 0) {
      for (const field of Object.keys(errs)) {
        clearErrorAfterDelay(field);
      }
      return;
    }

    setLoading(true);
    try {
      await onSubmit(form, setErrors);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    Object.keys(errors).forEach(field => {
      if (errors[field]) {
        clearErrorAfterDelay(field);
      }
    });
  }, [errors]);

  return (
    <div className="min-vh-100 d-flex flex-column align-items-center justify-content-center" style={{ background: '#f6f8fa' }}>
      <div
        className="shadow px-4 py-3"
        style={{
          width: 370,
          maxWidth: '98vw',
          background: '#fff',
          position: 'relative',
          overflow: 'visible',
          borderRadius: theme.cardRadius
        }}
      >
        {/* Top Back Button — always shown; destination follows getPostAuthDestination() so it
            goes home instead of a disabled /sign-in 404, same rule as the Email Verification
            flow's OK button. */}
        <Button
          mode="bordered"
          size="sm"
          onClick={() => navigate(getPostAuthDestination())}
          className="position-absolute"
          style={{
            top: 20,
            left: 20,
            borderRadius: '50%',
            width: 38,
            height: 38,
            padding: 0,
            minWidth: 0
          }}
        >
          <i className="bi bi-arrow-left fs-5" />
        </Button>

        {/* Logo and App Name */}
        <div className="d-flex flex-column align-items-center mb-2 mt-3">
          {!branding?.logo && <VelLogo size={60} />}
          {branding?.logo && <img
            src={branding.logo}
            alt={envConfig.appName}
            style={{
              height: 60,
              borderRadius: 14,
              background: '#fff',
              boxShadow: '0 2px 12px rgba(70, 48, 125, 0.11)',
              padding: '6px 20px'
            }}
          />}
          <h2
            style={{
              color: theme.primary,
              fontWeight: 700,
              letterSpacing: 1.1,
              marginTop: 16
            }}
          >
            {envConfig.appName}
          </h2>
        </div>

        <form onSubmit={handleSubmit} style={{ position: 'relative', zIndex: 1 }}>
          <h4 className="mb-2 fw-semibold text-center" style={{ color: theme.primary }}>
            Forgot Password
          </h4>
          <p style={{ fontSize: 14, textAlign: 'center', marginBottom: '1rem' }}>
            Enter your username or email. If an account exists, we&apos;ll send a reset link to the registered email.
          </p>

          {/* Identifier Field */}
          <div className="mb-3">
            <label
              htmlFor="identifier"
              className="form-label fw-semibold"
              style={{ color: theme.primary, fontSize: 15 }}
            >
              Username or Email
            </label>
            <input
              type="text"
              id="identifier"
              name="identifier"
              className="form-control"
              placeholder="Enter your username or email"
              value={form.identifier}
              onChange={handleChange}
              style={{
                borderRadius: theme.buttonRadius,
                borderColor: theme.primary + '55'
              }}
              autoComplete="username"
            />
            {errors.identifier && (
              <div style={{ color: "#c80b23", fontSize: 13, marginTop: 4 }}>
                {errors.identifier}
              </div>
            )}
          </div>

          {/* Send Reset Link Button */}
          <div className="d-grid mt-2">
            <Button
              mode="bordered"
              type="submit"
              disabled={loading}
            >
              {loading ? 'Sending...' : 'Send reset link'}
            </Button>
          </div>
        </form>

        {/* Bottom Gradient Bar */}
        <div
          style={{
            height: 8,
            width: '100%',
            borderBottomLeftRadius: '1.5rem',
            borderBottomRightRadius: '1.5rem',
            background: `linear-gradient(90deg, ${theme.primary}, ${theme.secondary})`,
            position: 'absolute',
            left: 0,
            bottom: 0,
            zIndex: 0
          }}
        />
      </div>
    </div>
  );
}

export default ForgotPasswordForm;
